import type { ModelManager, ModelManagerState } from '../models/ModelManager';
import type { ViewerModel } from '../models/types';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
}

function statusText(model: ViewerModel): string {
  if (model.status === 'loading') return 'Processing locally…';
  if (model.status === 'error') return model.error ?? 'Loading failed';
  return `${model.geometryCount.toLocaleString()} geometry items`;
}

export class ModelList {
  private state: ModelManagerState = { models: [], busyCount: 0 };

  constructor(
    private readonly container: HTMLElement,
    private readonly manager: ModelManager,
  ) {
    manager.changed.subscribe((state) => {
      this.state = state;
      this.render();
    });
    this.render();
  }

  private render(): void {
    this.container.replaceChildren();
    if (this.state.models.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'model-list__empty';
      empty.textContent = 'No IFC models loaded yet.';
      this.container.append(empty);
      return;
    }

    for (const model of this.state.models) {
      const row = document.createElement('article');
      row.className = `model-card model-card--${model.status}`;

      const visibility = document.createElement('input');
      visibility.type = 'checkbox';
      visibility.checked = model.visible;
      visibility.disabled = model.status === 'error';
      visibility.setAttribute('aria-label', `Show ${model.name}`);
      visibility.addEventListener('change', () => {
        this.manager.setVisibility(model.id, visibility.checked);
      });

      const details = document.createElement('div');
      details.className = 'model-card__details';
      const name = document.createElement('strong');
      name.title = model.name;
      name.textContent = model.name;
      const meta = document.createElement('span');
      meta.textContent = `${formatBytes(model.byteSize)} · ${statusText(model)}`;
      details.append(name, meta);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'icon-button';
      remove.title = `Remove ${model.name}`;
      remove.setAttribute('aria-label', `Remove ${model.name}`);
      remove.textContent = '×';
      remove.addEventListener('click', () => this.manager.remove(model.id));

      row.append(visibility, details, remove);
      this.container.append(row);
    }
  }
}

